import type {ReactNode} from 'react';
import {format} from 'date-fns';
import {ArrowUpRight} from 'lucide-react';
import {cn} from '@/lib/utils';
import {Tooltip, TooltipContent, TooltipTrigger} from '@/shared/ui/tooltip';
import {
  getDateFnsDateTimePattern,
  formatDisplayTimeOnly,
  formatDisplayTimeRange,
} from '@/utils/timeDisplayFormat';
import {getTimezoneAbbreviation} from '@/utils/timezoneUtils';
import {SESSION_STATUS_CARD_CONFIG} from '../model/constants';
import {
  SESSION_BUCKET_SHORT_LABEL,
  formatSessionStatusDisplay,
  type ScheduleLike,
  type SessionDistribution,
} from '../model/utils';
import {STATUS_PILL_CLASS} from '@/shared/lib/status-visual-palette';

export interface SessionOptionRadioCardProps {
  selected: boolean;
  label: string;
  sub?: string;
  trailing?: ReactNode;
  onSelect: () => void;
}

/**
 * Bordered radio card used by the session decision dialogs (Cancel, Bulk Cancel,
 * Reschedule) so every "how should this be processed?" choice reads the same.
 * `aria-label` (not the card's text content) carries the accessible name — the
 * sub-line and trailing badge would otherwise be folded into it.
 */
export function SessionOptionRadioCard({
  selected,
  label,
  sub,
  trailing,
  onSelect,
}: Readonly<SessionOptionRadioCardProps>) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-label={label}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
      )}
    >
      <div
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
          selected ? 'border-primary' : 'border-muted-foreground',
        )}
      >
        {selected && <div className="h-2 w-2 rounded-full bg-primary" />}
      </div>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {sub && <span className="block text-xs text-muted-foreground">{sub}</span>}
      </span>
      {trailing}
    </div>
  );
}

export interface TruncatedTextProps {
  /** Visible text — clipped with an ellipsis when it overflows its container. */
  text: string;
  /** Full tooltip content shown on hover/focus; defaults to `text` when omitted. */
  tooltip?: string;
  className?: string;
  as?: 'span' | 'div';
}

/**
 * Single-line truncated text (`overflow-hidden` + ellipsis) that always shows
 * its full value in a styled tooltip on hover/focus — used on every grid cell
 * whose content is unbounded free text (names, addresses, notes, etc.) so a
 * long value clips instead of stretching the column, per the "column widths
 * remain stable" requirement.
 */
export function TruncatedText({text, tooltip, className, as = 'span'}: Readonly<TruncatedTextProps>) {
  const Tag = as;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Tag tabIndex={0} className={cn('block truncate outline-none', className)}>
          {text}
        </Tag>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs break-words">
        {tooltip ?? text}
      </TooltipContent>
    </Tooltip>
  );
}

/** Expanded session table + card: same column titles as the (removed) nested grid header; reused by SessionView. */
export const SESSION_EXPAND_COLUMN_LABELS = {
  sessionName: 'Session Name',
  sessionType: 'Session Type',
  sessionStatus: 'Session Status',
  dateTime: 'Date & Time',
  actualSlot: 'Actual Slot',
  primaryModerator: 'Primary Moderator',
  meetingPoint: 'Meeting Point',
  secondaryModerator: 'Secondary Moderator',
  approvalStatus: 'Approval Status',
  paymentStatus: 'Payment Status',
  notes: 'Notes',
  actions: 'Actions',
} as const;

export const isMissingValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
    // Non-primitive values (objects, arrays, etc.) are never "missing"; stringifying them
    // would just produce a meaningless "[object Object]"-style result.
    return false;
  }
  const str = String(value).trim();
  return str === '' || str === '-' || str === '—';
};

export const renderCenteredDash = (className = 'text-xs text-slate-600') => (
  <span className={`inline-block ${className}`}>-</span>
);

/** Meeting point / venue line for a schedule row (matches BookedSessions `formatLocation` behavior). */
export function formatMeetingPoint(schedule: ScheduleLike | undefined): string {
  if (!schedule) return '';
  const raw = schedule.location as string | { name?: string } | undefined;
  let locationName: string | null = null;
  if (typeof raw === 'string' && raw.trim()) {
    locationName = raw.trim();
  } else if (raw && typeof raw === 'object' && raw.name) {
    locationName = String(raw.name).trim();
  }
  if (locationName && locationName.toUpperCase() === 'TBD') {
    return 'Not specified';
  }
  const locationParts: string[] = [];
  if (locationName) locationParts.push(locationName);
  const addressParts: string[] = [];
  if (schedule.street_address?.trim()) addressParts.push(schedule.street_address.trim());
  if (schedule.city?.trim()) addressParts.push(schedule.city.trim());
  if (schedule.state?.trim()) addressParts.push(schedule.state.trim());
  if (schedule.zipcode?.trim()) addressParts.push(schedule.zipcode.trim());
  if (schedule.country?.trim()) addressParts.push(schedule.country.trim());
  const fullAddress = addressParts.join(', ');
  if (fullAddress) locationParts.push(fullAddress);
  return locationParts.length > 0 ? locationParts.join(', ') : 'Not specified';
}

export const formatDateTime = (
  startTime?: string,
  endTime?: string,
  sessionType?: { timezone?: string }
): string => {
  if (!startTime) return '—';
  try {
    const startDate = new Date(startTime);
    const sessionTimezone = sessionType?.timezone;
    if (sessionTimezone) {
      const datePart = new Intl.DateTimeFormat('en-US', {
        timeZone: sessionTimezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(startDate);
      // Show the full start–end range with timezone (e.g. "6:00 PM - 6:27 PM PDT"),
      // matching the Booked Sessions view. Fall back to start-only if no end time.
      if (endTime) {
        return `${datePart} ${formatDisplayTimeRange(startTime, endTime, sessionTimezone, true)}`;
      }
      const timePart = formatDisplayTimeOnly(startDate, sessionTimezone);
      const tzAbbr = getTimezoneAbbreviation(sessionTimezone, startDate);
      return tzAbbr
        ? `${datePart} ${timePart} ${tzAbbr}`
        : `${datePart} ${timePart}`;
    }
    return format(startDate, getDateFnsDateTimePattern('MMM d, yyyy'));
  } catch {
    return '—';
  }
};

/** Bucket -> KPI color (mirrors SESSION_STATUS_CARD_CONFIG so mini-chips match the KPI strip). */
const BUCKET_COLOR = new Map(
  SESSION_STATUS_CARD_CONFIG.map((c) => [c.key, c.color] as const),
);

/** Dashed neutral tone for the two buckets with no KPI-card color (matches the existing "open slot" treatment). */
const OPEN_SLOT_CHIP_CLASS =
  'border border-dashed border-amber-300 bg-amber-50/80 text-amber-800';

function miniChipClassName(bucket: string): string {
  const color = BUCKET_COLOR.get(bucket as never);
  if (!color) return OPEN_SLOT_CHIP_CLASS;
  return cn('border', STATUS_PILL_CLASS[color]);
}

/** Full display label for a mini-chip's tooltip, incl. the two synthetic buckets. */
function bucketFullLabel(bucket: string): string {
  if (bucket === 'to_be_scheduled') return 'To Be Scheduled';
  if (bucket === 'invited') return 'Invited';
  return formatSessionStatusDisplay(bucket);
}

export interface SessionStatusDistributionChipsProps {
  distribution: SessionDistribution;
  /** Renders the "N sessions" chip as a clickable link (jump to Session view scoped to this participant). */
  clickable?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * "N sessions ↗" chip + a row of mini-chips summarizing the status distribution
 * across a participant's eligible session types (AC-2 smart-render + the
 * Session view's per-group header).
 */
export function SessionStatusDistributionChips({
  distribution,
  clickable,
  onClick,
  className,
}: Readonly<SessionStatusDistributionChipsProps>) {
  const entries = Object.entries(distribution.counts).filter(([, count]) => (count ?? 0) > 0);
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {clickable ? (
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-100"
        >
          {distribution.total} sessions
          <ArrowUpRight className="h-3 w-3" aria-hidden />
        </button>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-800">
          {distribution.total} sessions
        </span>
      )}
      <div className="flex flex-wrap items-center gap-1">
        {entries.map(([bucket, count]) => (
          <span
            key={bucket}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
              miniChipClassName(bucket),
            )}
            title={bucketFullLabel(bucket)}
          >
            <b>{count}</b> {SESSION_BUCKET_SHORT_LABEL[bucket] ?? bucket}
          </span>
        ))}
      </div>
    </div>
  );
}
